import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export type TeamRole = 'owner' | 'member';
export type StatementEffect = 'allow' | 'deny';

export interface Team {
  id: string;
  orgId: string;
  name: string;
  description?: string | null;
  /** The Org team: every member belongs to it and it cannot be deleted. */
  isDefault: boolean;
  status: string;
  memberCount: number;
  createdAt?: string | null;
}

export interface TeamMemberRow {
  memberId: string;
  userId: string;
  teamRole: TeamRole;
  orgRoleName?: string | null;
  email?: string | null;
  displayName?: string | null;
  /** Team roles this member holds in the team. */
  roleIds?: string[];
}

/** One IAM-style statement. Resource is `*`, `agent/*` or `agent/<id>`. */
export interface TeamStatement {
  effect: StatementEffect;
  action: string;
  resource: string;
}

/** A role defined inside one team; its grants usually name permission sets. */
export interface TeamRoleDefinition {
  id: string;
  teamId: string;
  name: string;
  description?: string | null;
  grants: TeamStatement[];
  memberCount: number;
}

export interface TeamDetail extends Team {
  members: TeamMemberRow[];
  statements: TeamStatement[];
  roles?: TeamRoleDefinition[];
}

export interface TeamAction {
  name: string;
  resourceType?: string | null;
  description?: string;
}

export interface TeamPermissions {
  teamId: string;
  statements: TeamStatement[];
  actions: TeamAction[];
}

function unwrapList<T>(response: unknown, key: string): T[] {
  const raw = response as Record<string, unknown> | unknown[];
  if (Array.isArray(raw)) return normalizeKeys<T[]>(raw);
  const inner = (raw?.response as Record<string, unknown> | undefined) ?? raw;
  const list = (inner as Record<string, unknown>)?.[key];
  return Array.isArray(list) ? normalizeKeys<T[]>(list) : [];
}

function unwrapObject<T>(response: unknown): T {
  const raw = response as { response?: unknown };
  const body = raw && typeof raw === 'object' && raw.response ? raw.response : response;
  return normalizeKeys<T>(body);
}

/** Statements go to the API as-is; their keys are already snake_case-safe. */
function statementBody(statements: TeamStatement[]) {
  return statements.map(({ effect, action, resource }) => ({ effect, action, resource }));
}

export const teamsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTeams: builder.query<Team[], string>({
      query: (orgId) => `/portal/organisations/${orgId}/teams`,
      transformResponse: (response: unknown) => unwrapList<Team>(response, 'teams'),
      providesTags: ['Team'],
    }),

    getTeam: builder.query<TeamDetail, { orgId: string; teamId: string }>({
      query: ({ orgId, teamId }) => `/portal/organisations/${orgId}/teams/${teamId}`,
      transformResponse: (response: unknown) => unwrapObject<TeamDetail>(response),
      providesTags: (_r, _e, { teamId }) => [{ type: 'Team', id: teamId }],
    }),

    createTeam: builder.mutation<
      TeamDetail,
      {
        orgId: string;
        name: string;
        description?: string;
        ownerMemberIds?: string[];
        memberIds?: string[];
      }
    >({
      query: ({ orgId, name, description, ownerMemberIds, memberIds }) => ({
        url: `/portal/organisations/${orgId}/teams`,
        method: 'POST',
        body: {
          name,
          description,
          owner_member_ids: ownerMemberIds,
          member_ids: memberIds,
        },
      }),
      transformResponse: (response: unknown) => unwrapObject<TeamDetail>(response),
      invalidatesTags: ['Team', 'Member'],
    }),

    updateTeam: builder.mutation<
      TeamDetail,
      { orgId: string; teamId: string; name?: string; description?: string }
    >({
      query: ({ orgId, teamId, name, description }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}`,
        method: 'PUT',
        body: { name, description },
      }),
      transformResponse: (response: unknown) => unwrapObject<TeamDetail>(response),
      invalidatesTags: ['Team', 'Member'],
    }),

    deleteTeam: builder.mutation<{ deleted: boolean }, { orgId: string; teamId: string }>({
      query: ({ orgId, teamId }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Team', 'Member'],
    }),

    addTeamMember: builder.mutation<
      TeamDetail,
      { orgId: string; teamId: string; memberId: string; teamRole?: TeamRole }
    >({
      query: ({ orgId, teamId, memberId, teamRole = 'member' }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}/members`,
        method: 'POST',
        body: { member_id: memberId, team_role: teamRole },
      }),
      transformResponse: (response: unknown) => unwrapObject<TeamDetail>(response),
      invalidatesTags: ['Team', 'Member'],
    }),

    updateTeamMember: builder.mutation<
      TeamDetail,
      { orgId: string; teamId: string; memberId: string; teamRole: TeamRole }
    >({
      query: ({ orgId, teamId, memberId, teamRole }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}/members/${memberId}`,
        method: 'PUT',
        body: { team_role: teamRole },
      }),
      transformResponse: (response: unknown) => unwrapObject<TeamDetail>(response),
      invalidatesTags: ['Team', 'Member'],
    }),

    removeTeamMember: builder.mutation<
      TeamDetail,
      { orgId: string; teamId: string; memberId: string }
    >({
      query: ({ orgId, teamId, memberId }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}/members/${memberId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => unwrapObject<TeamDetail>(response),
      invalidatesTags: ['Team', 'Member'],
    }),

    getTeamPermissions: builder.query<TeamPermissions, { orgId: string; teamId: string }>({
      query: ({ orgId, teamId }) =>
        `/portal/organisations/${orgId}/teams/${teamId}/permissions`,
      transformResponse: (response: unknown) => unwrapObject<TeamPermissions>(response),
      providesTags: (_r, _e, { teamId }) => [{ type: 'Team', id: `${teamId}:permissions` }],
    }),

    putTeamPermissions: builder.mutation<
      TeamPermissions,
      { orgId: string; teamId: string; statements: TeamStatement[] }
    >({
      query: ({ orgId, teamId, statements }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}/permissions`,
        method: 'PUT',
        body: { statements: statementBody(statements) },
      }),
      transformResponse: (response: unknown) => unwrapObject<TeamPermissions>(response),
      invalidatesTags: (_r, _e, { teamId }) => [
        'Team',
        'Agent',
        { type: 'Team', id: teamId },
        { type: 'Team', id: `${teamId}:permissions` },
      ],
    }),

    createTeamRole: builder.mutation<
      TeamRoleDefinition,
      {
        orgId: string;
        teamId: string;
        name: string;
        description?: string;
        grants: TeamStatement[];
      }
    >({
      query: ({ orgId, teamId, name, description, grants }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}/roles`,
        method: 'POST',
        body: { name, description, grants: statementBody(grants) },
      }),
      transformResponse: (response: unknown) => unwrapObject<TeamRoleDefinition>(response),
      invalidatesTags: (_r, _e, { teamId }) => ['Agent', { type: 'Team', id: teamId }],
    }),

    updateTeamRole: builder.mutation<
      TeamRoleDefinition,
      {
        orgId: string;
        teamId: string;
        roleId: string;
        name?: string;
        description?: string;
        grants?: TeamStatement[];
      }
    >({
      query: ({ orgId, teamId, roleId, name, description, grants }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}/roles/${roleId}`,
        method: 'PUT',
        body: { name, description, grants: grants ? statementBody(grants) : undefined },
      }),
      transformResponse: (response: unknown) => unwrapObject<TeamRoleDefinition>(response),
      invalidatesTags: (_r, _e, { teamId }) => ['Agent', { type: 'Team', id: teamId }],
    }),

    deleteTeamRole: builder.mutation<
      { deleted: boolean },
      { orgId: string; teamId: string; roleId: string }
    >({
      query: ({ orgId, teamId, roleId }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}/roles/${roleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { teamId }) => [
        'Agent',
        'Member',
        { type: 'Team', id: teamId },
      ],
    }),

    setMemberTeamRoles: builder.mutation<
      TeamDetail,
      { orgId: string; teamId: string; memberId: string; roleIds: string[] }
    >({
      query: ({ orgId, teamId, memberId, roleIds }) => ({
        url: `/portal/organisations/${orgId}/teams/${teamId}/members/${memberId}/roles`,
        method: 'PUT',
        body: { role_ids: roleIds },
      }),
      transformResponse: (response: unknown) => unwrapObject<TeamDetail>(response),
      invalidatesTags: (_r, _e, { teamId }) => [
        'Agent',
        'Member',
        { type: 'Team', id: teamId },
      ],
    }),
  }),
});

export const {
  useGetTeamsQuery,
  useGetTeamQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useAddTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useGetTeamPermissionsQuery,
  usePutTeamPermissionsMutation,
  useCreateTeamRoleMutation,
  useUpdateTeamRoleMutation,
  useDeleteTeamRoleMutation,
  useSetMemberTeamRolesMutation,
} = teamsApi;
